import AbstractSubject from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import { type PeriodDataApiType } from '@naturehouse/design-system/components/atoms/period/models/AbstractPeriodModel';
import LocalStorageStrategy from '@naturehouse/nh-essentials/lib/storage/strategies/LocalStorageStrategy';
import ClientStorage from '@naturehouse/nh-essentials/lib/storage/Storage';

import type AbstractStorageStrategy from '@naturehouse/nh-essentials/lib/storage/strategies/AbstractStorageStrategy';
import { CollectionEvents } from '../../common/CollectionBase';
import PeriodBookingCollection from '../periodCalendar/PeriodBookingCollection';
import PeriodBlockCollection from '../periodCalendar/PeriodBlockCollection';
import PeriodConfigurationCollection from '../periodCalendar/PeriodConfigurationCollection';
import type { BasePeriodCollectionParamsType } from '../periodCalendar/BasePeriodCollectionParams.type';

export enum PeriodStorageCollectionType {
    BOOKING = 'PeriodBookingCollection',
    BLOCK = 'PeriodBlockCollection',
    CONFIGURATION = 'PeriodConfigurationCollection'
}

export enum PeriodStorageType {
    BOOKING = 'bookings',
    BLOCK = 'blocks',
    CONFIGURATION = 'configurations'
}

/** @TODO Specify API Return types and replace all `unknown` */
export default class PeriodStorageManager extends AbstractSubject {
    public static readonly DATABASE_NAME: string = 'LandlordCalendarPeriods';

    static #instance: PeriodStorageManager | null = null;

    public static getInstance(): PeriodStorageManager {
        if (this.#instance === null) {
            this.#instance = new PeriodStorageManager();
        }

        return this.#instance;
    }

    readonly #bookingCollection = PeriodBookingCollection.getInstance();

    readonly #blockCollection = PeriodBlockCollection.getInstance();

    readonly #configurationCollection = PeriodConfigurationCollection.getInstance();

    private constructor() {
        super();
        this.#bookingCollection.attach(this);
        this.#blockCollection.attach(this);
        this.#configurationCollection.attach(this);
    }

    public async update(event: CustomEvent): Promise<void> {
        const storage = await this.#getStorage();
        const storageKey = this.#getStorageKey(event.detail.type);
        switch (event.type) {
            case CollectionEvents.ITEM_ADDED:
                await this.#handleAdd(storage, storageKey, event.detail.data);
                break;
            case CollectionEvents.ITEM_UPDATED:
                await this.#handleUpdate(storage, storageKey, event.detail.data);
                break;
            case CollectionEvents.ITEM_DELETED:
                await this.#handleDelete(storage, storageKey, event.detail.data);
                break;
            default:
                break;
        }
    }

    async retrievePeriods(params: BasePeriodCollectionParamsType): Promise<void> {
        const storage = await this.#getStorage();
        await storage.clear();

        const bookings = await this.#bookingCollection.retrieve(params);
        await storage.setItem(PeriodStorageType.BOOKING, bookings ?? []);

        const blocks = await this.#blockCollection.retrieve(params);
        await storage.setItem(PeriodStorageType.BLOCK, blocks ?? []);

        const configurations = await this.#configurationCollection.retrieve(params);
        await storage.setItem(PeriodStorageType.CONFIGURATION, configurations ?? []);

        this.notify();
    }

    public async getStoredPeriod(
        type: PeriodStorageType,
        id: string
    ): Promise<PeriodDataApiType<unknown> | undefined> {
        const storage = await this.#getStorage();
        const periods = (await storage.getItem(type)) ?? [];

        return periods.find((period): boolean => String(period.id) === String(id));
    }

    async #getStorage(): Promise<AbstractStorageStrategy<PeriodDataApiType<unknown>[]>> {
        return ClientStorage.retrieveStorage<PeriodDataApiType<unknown>[]>(
            PeriodStorageManager.DATABASE_NAME,
            LocalStorageStrategy
        );
    }

    async #handleAdd(
        storage: AbstractStorageStrategy<PeriodDataApiType<unknown>[]>,
        storageKey: string,
        data: PeriodDataApiType<unknown>
    ): Promise<void> {
        const value = (await storage.getItem(storageKey)) || [];
        value.push(data);
        await storage.updateItem(storageKey, value);
        this.notify();
    }

    async #handleUpdate(
        storage: AbstractStorageStrategy<PeriodDataApiType<unknown>[]>,
        storageKey: string,
        data: PeriodDataApiType<unknown>
    ): Promise<void> {
        const value = (await storage.getItem(storageKey)) || [];
        const periodToUpdate = value.find((period) => period.id === data.id);

        if (!periodToUpdate) {
            throw new Error('Period to update not found in storage');
        }

        const updatedValue = value.map((period) => ({ ...period, ...data }));
        await storage.updateItem(storageKey, updatedValue);
        this.notify();
    }

    async #handleDelete(
        storage: AbstractStorageStrategy<PeriodDataApiType<unknown>[]>,
        storageKey: string,
        data: PeriodDataApiType<unknown>
    ): Promise<void> {
        const value = await storage.getItem(storageKey);

        if (!value) {
            return;
        }

        const updatedValue = value.filter((period) => period.id !== data.id);

        await storage.updateItem(storageKey, updatedValue);
        this.notify();
    }

    #getStorageKey(type: PeriodStorageCollectionType): string {
        switch (type) {
            case PeriodStorageCollectionType.BLOCK: {
                return PeriodStorageType.BLOCK;
            }
            case PeriodStorageCollectionType.BOOKING: {
                return PeriodStorageType.BOOKING;
            }
            case PeriodStorageCollectionType.CONFIGURATION: {
                return PeriodStorageType.CONFIGURATION;
            }
            default: {
                throw new Error('Invalid storage type provided');
            }
        }
    }
}
